import { Injectable } from '@angular/core';
import { Papa } from 'ngx-papaparse';

const DISALLOWED_CHARACTERS = new Set<string>('=+-@'.split(''));

const BOM = '\uFEFF';

type CsvFile = string | Blob;

@Injectable()
export class CsvFormatterService {
  constructor(private papa: Papa) {}

  getCsvFileHeaders(file: CsvFile): string[] {
    let headers: string[] = [];

    this.papa.parse(file, {
      header: true,
      complete: (parsedCsvResponce) => {
        headers = parsedCsvResponce.meta.fields;
      },
    });

    return headers;
  }

  getCsvFileData(file: CsvFile): object[] {
    return this.papa.parse(file, { header: true, skipEmptyLines: true }).data;
  }

  isCsvKeysUnique(file: CsvFile, keys: string[]): boolean {
    let isUnique: boolean;
    this.papa.parse(file, {
      header: true,
      complete: (parsedCsvResponce) => {
        const uniqueData = new Set(
          parsedCsvResponce.data.map((data) => keys.reduce((res, curr) => `${res}${data[curr]}`, '')),
        );
        isUnique = uniqueData.size === parsedCsvResponce.data.length;
      },
    });
    return isUnique;
  }

  createCsvBlob(rows: any[][], headers?: string[]): Blob {
    const headersString = `${headers?.join(',')}\n`;
    const dataString = rows.map((row) => this.processRow(row)).join('\n');
    const csvString = BOM + (headers?.length ? headersString + dataString : dataString);
    return new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  }

  private processRow(cells: any[]): string {
    return cells
      .map((cell) => cell ?? '')
      .map((cell) => this.convertToDateStringIfNeeded(cell))
      .map((cell) => this.replaceQuotesWithDoubleQuotes(cell))
      .map((cell) => this.replaceNewLineWithSpace(cell))
      .map((cell) => this.wrapCellWithDoubleQuotesIfNeeded(cell))
      .map((cell) => this.sanitizeCell(cell))
      .map((cell) => this.replaceApostropheWithSingleQuoteMark(cell))
      .map((cell) => this.replaceCommaLikeQuotesWithRegularOnes(cell))
      .join(',');
  }

  /**
   * prevents a cell from being treated as a formula in Excel, Google Sheets, etc
   */
  private sanitizeCell(cell: string): string {
    const firstCharacterFromCell = cell[0];

    if (DISALLOWED_CHARACTERS.has(firstCharacterFromCell)) {
      return `'${cell}`;
    }

    return cell;
  }

  /**
   * returns locale date string if cell is Date, in other cases returns convert cell to simple strings
   */
  private convertToDateStringIfNeeded(cell: any): string {
    if (cell instanceof Date) {
      return cell.toLocaleString();
    }

    return cell.toString();
  }

  /**
   * replaces apostrophe (u2019) with single quote mark
   */
  private replaceApostropheWithSingleQuoteMark(cell: string): string {
    return cell.replace(/(\u2019)/g, '\'');
  }

  /**
   * wraps cell with double quotes if it contains special characters (",,,-)
   */
  private wrapCellWithDoubleQuotesIfNeeded(cell: string): string {
    if (cell.search(/("|,|-)/g) >= 0) {
      return `"${cell}"`;
    }
    return cell;
  }

  private replaceQuotesWithDoubleQuotes(cell: string): string {
    return cell.replace(/"/g, '""');
  }

  private replaceNewLineWithSpace(cell: string): string {
    return cell.replace(/\n/g, ' ');
  }

  /**
   * replaces apostrophe (u201C and u201D, “ and ”) with double quote mark
   */
  private replaceCommaLikeQuotesWithRegularOnes(cell: string): string {
    return cell.replace(/(\u201C)/g, '""').replace(/(\u201D)/g, '""');
  }
}
